import React, { useCallback, useMemo, useState } from 'react';
import { Box, LeftRow, Subtitle, Title } from '../styles';
import { BottomSection, BottomSubSections, ChipSection, Data, DropText, GroupDiv, PropertiesSection } from './styles';
import { Dropdown, DropdownItem } from '../../dropdown';
import {
  CHIP_SIZE,
  CHIP_TYPE,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_COMPLETED_TASK_FILTER_NAME,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_NAME,
  SHOW_TASK_GROUPING_TYPE,
  SHOW_TASK_ORDER_NAME,
  TASK_LIST_TYPE,
  TASK_LIST_TYPE_LABEL,
  TASK_FILTER_PROPERTY_TYPE,
  CHIP_ICON_POSITION
} from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import { completedTaskMenu, menuViewsItems } from '../../../global/row';
import { updateProjectFilterGrouping, updateProjectFilterOrder } from '../../../helpers/common';
import { NewTaskFilterInterface } from '../../../interfaces/TaskInterface';
import { observer } from 'mobx-react-lite';
import { dispatchProjectSuccess, updateProjectFilter } from '../../../services/projectServices';
import Chip from '../../chipComponent';
import SwitchInput from '../../input/switchInput';

const TasksFilterSettingCard = () => {
  // States
  const [openDropdown, setOpenDropdown] = useState('');

  // Mobx store
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;
  const { projectFilterType } = currentProject || {};

  const handleFilterChange = useCallback(
    async (propsName: keyof NewTaskFilterInterface, value: number | boolean) => {
      if (!currentProject) return;
      try {
        setOpenDropdown('');
        currentProject.updateProjectFilterType(propsName, value);

        const payload = {
          [propsName]: value
        };

        const response = await updateProjectFilter(currentProject.id, payload);
        if (response) {
          dispatchProjectSuccess('Details updated successfully');
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [currentProject]
  );

  const handleOutsideClick = useCallback(() => {
    setOpenDropdown('');
  }, []);

  // completed task dropdown items
  const completedDropdown = useMemo(() => {
    return completedTaskMenu.map((item) => (
      <DropdownItem
        key={item.key}
        label={item.label}
        onClick={() => handleFilterChange('showCompletedTaskByFilter', item?.key)}
        isActive={projectFilterType?.showCompletedTaskByFilter === item?.key}
      />
    ));
  }, [handleFilterChange, projectFilterType?.showCompletedTaskByFilter]);

  // views dropdown items
  const viewsDropdown = useMemo(() => {
    return menuViewsItems.map((item) => (
      <DropdownItem
        key={item.key}
        label={item.label}
        onClick={() => handleFilterChange('taskViewType', item?.key)}
        isActive={projectFilterType?.taskViewType === item?.key}
      />
    ));
  }, [handleFilterChange, projectFilterType?.taskViewType]);

  // get ordering menu items for filtering
  const orderingMenuItems = useCallback(() => {
    const items = [];

    // Add 'Custom' option if grouping status matches certain types and view type is not 'Calendar View'.
    if (
      (projectFilterType?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS ||
        projectFilterType?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP ||
        projectFilterType?.groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT) &&
      projectFilterType?.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW
    ) {
      items.push({ key: SHOW_TASK_BY_ORDER.CUSTOM, label: 'Custom' });
    }

    // Add 'Priority' option if priority is enabled.
    if (currentProject?.priority?.enabled) {
      items.push({ key: SHOW_TASK_BY_ORDER.PRIORITY, label: 'Priority' });
    }

    // Add 'Due Date' option if view type is not 'Calendar View'.
    if (projectFilterType?.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW) {
      items.push({ key: SHOW_TASK_BY_ORDER.DUE_DATE, label: 'Due Date' });
    }

    // Always include 'Created Date'.
    items.push({ key: SHOW_TASK_BY_ORDER.CREATED_DATE, label: 'Created Date' });

    return items;
  }, [currentProject?.priority?.enabled, projectFilterType?.groupingStatus, projectFilterType?.taskViewType]);
  // ordering dropdown  items
  const orderingMenuDropdown = useMemo(() => {
    const orderingMenuData = orderingMenuItems();
    return orderingMenuData.map((item) => (
      <DropdownItem
        key={item.key}
        label={item.label}
        onClick={() => handleFilterChange('orderByStatus', item?.key)}
        isActive={projectFilterType?.orderByStatus === item?.key}
      />
    ));
  }, [handleFilterChange, orderingMenuItems, projectFilterType?.orderByStatus]);

  const getOrderName = useCallback(() => {
    const orderType = updateProjectFilterOrder(currentProject!);
    return SHOW_TASK_ORDER_NAME[orderType];
  }, [currentProject]);

  const getGroupName = useCallback(() => {
    const groupingType = updateProjectFilterGrouping(currentProject!);
    return SHOW_TASK_GROUPING_NAME[groupingType || 1];
  }, [currentProject]);

  // grouping dropdown items
  const groupMenuItems = [
    { key: SHOW_TASK_GROUPING_TYPE.NO_GROUPING, label: 'No Grouping' },
    currentProject?.isGroupEnabled && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP, label: 'Group' },
    currentProject?.priority?.enabled && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY, label: 'Priority' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER, label: 'User' },
    currentProject?.isMilestone && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE, label: 'Milestone' },
    currentProject?.statusEnable &&
      currentProject?.statusData?.length > 0 && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS, label: 'Status' }
  ].filter((item): item is { key: number; label: string } => Boolean(item));

  // grouping dropdown
  const groupingDropdown = useMemo(() => {
    return groupMenuItems.map((item) => (
      <DropdownItem
        key={item?.key}
        label={item.label}
        onClick={() => handleFilterChange('groupingStatus', item?.key)}
        isActive={projectFilterType?.groupingStatus === item?.key}
      />
    ));
  }, [groupMenuItems, handleFilterChange, projectFilterType?.groupingStatus]);

  const getDisplayProperties = useCallback(() => {
    const properties = [];
    const viewType = projectFilterType?.taskViewType;

    if (viewType === TASK_LIST_TYPE.TASKS) {
      if (currentProject?.identifier) properties.push(TASK_FILTER_PROPERTY_TYPE.ID);
      properties.push(TASK_FILTER_PROPERTY_TYPE.STATUS);
      if (currentProject?.priority?.enabled) properties.push(TASK_FILTER_PROPERTY_TYPE.PRIORITY);
      properties.push(TASK_FILTER_PROPERTY_TYPE.ASSIGNEE);
      if (currentProject?.labelsEnabled) properties.push(TASK_FILTER_PROPERTY_TYPE.LABELS);
      properties.push(TASK_FILTER_PROPERTY_TYPE.DUE_DATE);
    } else if (viewType === TASK_LIST_TYPE.TASK_GRID) {
      if (currentProject?.identifier) properties.push(TASK_FILTER_PROPERTY_TYPE.ID);
      properties.push(TASK_FILTER_PROPERTY_TYPE.STATUS);
      if (currentProject?.priority?.enabled) properties.push(TASK_FILTER_PROPERTY_TYPE.PRIORITY);
      properties.push(TASK_FILTER_PROPERTY_TYPE.ASSIGNEE);
      if (currentProject?.labelsEnabled) properties.push(TASK_FILTER_PROPERTY_TYPE.LABELS);
      if (currentProject?.isMilestone) properties.push(TASK_FILTER_PROPERTY_TYPE.MILESTONE);
      properties.push(TASK_FILTER_PROPERTY_TYPE.DUE_DATE);
    } else if (viewType === TASK_LIST_TYPE.CALENDAR_VIEW) {
      if (currentProject?.identifier) properties.push(TASK_FILTER_PROPERTY_TYPE.ID);
      if (currentProject?.priority?.enabled) properties.push(TASK_FILTER_PROPERTY_TYPE.PRIORITY);
      properties.push(TASK_FILTER_PROPERTY_TYPE.ASSIGNEE);
      if (currentProject?.labelsEnabled) properties.push(TASK_FILTER_PROPERTY_TYPE.LABELS);
    }

    return properties;
  }, [currentProject, projectFilterType?.taskViewType]);

  const handleDisplayPropertyToggle = useCallback(
    async (propertyType: string) => {
      if (!currentProject?.id) return;
      const currentProjectDisplayFilter = currentProject?.taskFilterType?.displayProperties;
      const currentValue = currentProjectDisplayFilter?.[propertyType] !== false;
      const newVisibility = !currentValue;

      const displayProperties = {
        [TASK_FILTER_PROPERTY_TYPE.ID]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.ID] ?? false,
        [TASK_FILTER_PROPERTY_TYPE.STATUS]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.STATUS] ?? true,
        [TASK_FILTER_PROPERTY_TYPE.PRIORITY]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.PRIORITY] ?? true,
        [TASK_FILTER_PROPERTY_TYPE.ASSIGNEE]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.ASSIGNEE] ?? true,
        [TASK_FILTER_PROPERTY_TYPE.LABELS]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.LABELS] ?? true,
        [TASK_FILTER_PROPERTY_TYPE.MILESTONE]:
          currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.MILESTONE] ?? true,
        [TASK_FILTER_PROPERTY_TYPE.DUE_DATE]: currentProjectDisplayFilter?.[TASK_FILTER_PROPERTY_TYPE.DUE_DATE] ?? true,
        // Update the toggled property with the new value
        [propertyType]: newVisibility
      };
      // Prepare payload for API
      const payload = {
        displayProperties: displayProperties
      };

      // Call API to persist
      const response = await updateProjectFilter(currentProject.id, payload);

      if (response) {
        dispatchProjectSuccess('Display properties updated successfully');
      }

      // Also update Firebase preference
      // const currentTaskPropertyFilter =
      //   (await getWorkspaceProjectPreferenceData(currentProject.id, 'list_items_display_filter')) || {};
      // await updateWorkspaceProjectPreference(currentProject.id, {
      //   list_items_display_filter: { ...(currentTaskPropertyFilter || {}), ...(displayProperties || {}) }
      // });
    },
    [currentProject?.id, currentProject?.taskFilterType?.displayProperties]
  );

  const renderTaskFilterProperties = useCallback(() => {
    const propertiesToShow = getDisplayProperties();
    return propertiesToShow.map((property) => {
      const isActive = currentProject?.taskFilterType?.displayProperties?.[property] !== false;
      return (
        <Chip
          key={property}
          title={property}
          variant={isActive ? CHIP_TYPE.FILL : CHIP_TYPE.FILL_WITHOUT_BORDER}
          size={CHIP_SIZE.SMALL}
          isActive={isActive}
          onClick={() => handleDisplayPropertyToggle(property)}
          textColorType='secondary'
        />
      );
    });
  }, [currentProject?.taskFilterType?.displayProperties, getDisplayProperties, handleDisplayPropertyToggle]);

  return (
    <Box>
      <LeftRow>
        <Title>Default Tasks View</Title>
        <Subtitle>Customize task visibility and sorting to streamline your view.</Subtitle>
      </LeftRow>
      <BottomSection>
        <BottomSubSections>
          {/* views dropdown */}
          <Data>
            <GroupDiv>
              <DropText>Views</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Views'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={TASK_LIST_TYPE_LABEL[projectFilterType?.taskViewType || TASK_LIST_TYPE.TASKS]}
                  onClick={() => setOpenDropdown('Views')}
                  isActive={openDropdown === 'Views'}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={openDropdown === 'Views' ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onClickItemTrigger={handleOutsideClick}
              onOutsideClick={handleOutsideClick}>
              {viewsDropdown}
            </Dropdown>
          </Data>
          {/* grouping dropdown */}
          <Data>
            <GroupDiv>
              <DropText>Grouping</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Grouping'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={getGroupName()}
                  onClick={() => setOpenDropdown('Grouping')}
                  isActive={openDropdown === 'Grouping'}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={openDropdown === 'Grouping' ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onClickItemTrigger={handleOutsideClick}
              onOutsideClick={handleOutsideClick}>
              {groupingDropdown}
            </Dropdown>
          </Data>
          {/* ordering dropdown */}
          <Data>
            <GroupDiv>
              <DropText>Ordering</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Ordering'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={getOrderName()}
                  onClick={() => setOpenDropdown('Ordering')}
                  isActive={openDropdown === 'Ordering'}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={openDropdown === 'Ordering' ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onClickItemTrigger={handleOutsideClick}
              onOutsideClick={handleOutsideClick}>
              {orderingMenuDropdown}
            </Dropdown>
          </Data>
        </BottomSubSections>
        <BottomSubSections>
          {/* task completed by regency */}
          <Data>
            <GroupDiv>
              <DropText>Task completed by recency</DropText>
            </GroupDiv>
            <SwitchInput
              checked={projectFilterType?.showRecentlyCompletedTask ?? true}
              onChange={() =>
                handleFilterChange('showRecentlyCompletedTask', !projectFilterType?.showRecentlyCompletedTask)
              }
            />
          </Data>
          {/* completed task dropdown */}
          <Data>
            <GroupDiv>
              <DropText>Completed tasks</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Completed tasks'
              content={
                <Chip
                  variant={CHIP_TYPE.FILL}
                  size={CHIP_SIZE.SMALL}
                  title={
                    SHOW_COMPLETED_TASK_FILTER_NAME[
                      projectFilterType?.showCompletedTaskByFilter || SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH
                    ]
                  }
                  onClick={() => setOpenDropdown('Completed tasks')}
                  isActive={openDropdown === 'Completed tasks'}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={openDropdown === 'Completed tasks' ? 'dropdown-icon' : ''}
                  iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                />
              }
              trigger='click'
              onClickItemTrigger={handleOutsideClick}
              onOutsideClick={handleOutsideClick}>
              {completedDropdown}
            </Dropdown>
          </Data>
        </BottomSubSections>
        <BottomSubSections>
          {/* show subtask section */}
          <Data>
            <GroupDiv>
              <DropText>Show sub-tasks</DropText>
            </GroupDiv>
            <SwitchInput
              checked={projectFilterType?.showSubTask ?? false}
              onChange={() => handleFilterChange('showSubTask', !projectFilterType?.showSubTask)}
            />
          </Data>
          {/* show empty groups section */}
          <Data>
            <GroupDiv>
              <DropText>Show empty groups</DropText>
            </GroupDiv>
            <SwitchInput
              checked={projectFilterType?.showEmptyGroup ?? false}
              onChange={() => handleFilterChange('showEmptyGroup', !projectFilterType?.showEmptyGroup)}
            />
          </Data>
        </BottomSubSections>
        <BottomSubSections>
          <PropertiesSection>
            <DropText>Display properties</DropText>
            <ChipSection>{renderTaskFilterProperties()}</ChipSection>
          </PropertiesSection>
        </BottomSubSections>
      </BottomSection>
    </Box>
  );
};

export default observer(TasksFilterSettingCard);
